import * as React from "react";
import {
    Typography,
    Box,
    Container,
    Button,
    CircularProgress,
    Chip,
    Paper,
    Autocomplete,
    ToggleButtonGroup,
    ToggleButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
} from "@mui/material";
import { connect } from "react-redux";
import { notification } from "antd";
import { muiTableSx } from "../../app/components/AntTableStyles";
import {
    sec_button,
    main_button,
    MainInput,
} from "../../app/components/Styles";
import { brandFilterOptions, brandOptionLabel } from "../../app/components/BrandSelect";
import ReactFileReader from "react-file-reader";
import Upload from "@mui/icons-material/Upload";
import { addPayouts, getCashCycles, getVendors, getPayoutsDue } from "../../app/store/actions/whFinanceActions";
import { AddCard, Delete } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import * as XLSX from 'xlsx';
import axios from "axios";

export const AddPayouts = ({ 
    addPayouts, 
    getCashCycles, 
    cashCycles, 
    isLoading,
    getVendors,
    vendors,
    getPayoutsDue,
    summary,
    token
}) => {
    const [file, setFile] = React.useState(null);
    const [selectedCashCycle, setSelectedCashCycle] = React.useState(null);
    const [payoutMode, setPayoutMode] = React.useState("excel"); // "excel" or "single"
    const [parsedPayouts, setParsedPayouts] = React.useState([]);
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    
    // Single vendor payout states
    const [selectedVendor, setSelectedVendor] = React.useState(null);
    const [payoutAmount, setPayoutAmount] = React.useState("");
    const [vendorDueAmount, setVendorDueAmount] = React.useState(null);
    const [loadingDueAmount, setLoadingDueAmount] = React.useState(false);
    
    // Password verification states
    const [passwordDialogOpen, setPasswordDialogOpen] = React.useState(false);
    const [password, setPassword] = React.useState('');
    const [passwordError, setPasswordError] = React.useState(null);
    const [passwordLoading, setPasswordLoading] = React.useState(false);
    const [pendingPayoutData, setPendingPayoutData] = React.useState(null);

    const navigate = useNavigate();

    React.useEffect(() => {
        getCashCycles({});
        getVendors();
    }, []);

    // Update vendor names when vendors are loaded
    React.useEffect(() => {
        if (vendors && vendors.length > 0 && parsedPayouts.length > 0) {
            setParsedPayouts(prevPayouts => {
                return prevPayouts.map(payout => {
                    const vendor = vendors.find(v => v._id === payout.vendorId);
                    return {
                        ...payout,
                        vendorName: vendor?.name || payout.vendorId
                    };
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendors]);

    React.useEffect(() => {
        if (selectedVendor) {
            setLoadingDueAmount(true);
            getPayoutsDue({ vendorId: selectedVendor._id, limit: 100 });
        } else {
            setVendorDueAmount(null);
            setPayoutAmount("");
        }
    }, [selectedVendor, getPayoutsDue]);

    React.useEffect(() => {
        if (summary?.docs && selectedVendor) {
            const vendorSummary = summary.docs.find(doc => doc.vendor?._id === selectedVendor._id);
            if (vendorSummary) {
                setVendorDueAmount(vendorSummary.totalDue);
                setLoadingDueAmount(false);
            } else if (summary.docs.length > 0) {
                // If we got results but this vendor isn't in them, they have 0 due
                setVendorDueAmount(0);
                setLoadingDueAmount(false);
            }
        }
    }, [summary, selectedVendor]);

    const handleDeleteFile = () => {
        setFile(null);
        setParsedPayouts([]);
        setShowConfirmation(false);
    };

    const handleRemovePayout = (index) => {
        if (parsedPayouts.length <= 1) {
            notification.warning({ message: "At least one payout must remain" });
            return;
        }
        const updatedPayouts = parsedPayouts.filter((_, i) => i !== index);
        setParsedPayouts(updatedPayouts);
    };

    const handleModeChange = (event, newMode) => {
        if (newMode !== null) {
            setPayoutMode(newMode);
            setFile(null);
            setParsedPayouts([]);
            setShowConfirmation(false);
            setSelectedVendor(null);
            setPayoutAmount("");
            setVendorDueAmount(null);
        }
    };

    const handleExcelSubmit = (event) => {
        event.preventDefault();
        
        if (!file) {
            notification.error({ message: "Please upload a valid Excel file (.xlsx or .xls)" });
            return;
        }

        if (!showConfirmation || parsedPayouts.length === 0) {
            notification.error({ message: "Please upload and parse the Excel file first" });
            return;
        }

        // Filter out zeros (negative amounts are allowed for collecting outstanding brand balances)
        const payouts = parsedPayouts
            .filter(p => p.amount !== 0)
            .map(p => ({
                vendor_id: p.vendorId,
                amount: p.amount
            }));

        if (payouts.length === 0) {
            notification.error({ message: "No valid payouts to submit. Amounts must be non-zero (negative amounts collect outstanding balances)." });
            return;
        }

        if (payouts.length < 1) {
            notification.error({ message: "At least one payout is required" });
            return;
        }

        const payoutData = { payouts, cashCycle: selectedCashCycle?.title };
        setPendingPayoutData(payoutData);
        setPasswordDialogOpen(true);
    };

    const handleSingleVendorSubmit = (event) => {
        event.preventDefault();
        
        if (!selectedVendor || !payoutAmount) {
            notification.error({ message: "Please fill all fields" });
            return;
        }

        const amount = parseFloat(payoutAmount);
        if (isNaN(amount) || amount === 0) {
            notification.error({ message: "Please enter a non-zero amount (use a negative amount to collect an outstanding balance)" });
            return;
        }

        const payouts = [{
            vendor_id: selectedVendor._id,
            amount: amount
        }];

        const payoutData = { payouts, cashCycle: selectedCashCycle?.title };
        setPendingPayoutData(payoutData);
        setPasswordDialogOpen(true);
    };

    const handlePasswordClose = () => {
        if (!passwordLoading) {
            setPasswordDialogOpen(false);
            setPassword('');
            setPasswordError(null);
            setPendingPayoutData(null);
        }
    };

    const handlePasswordConfirm = async () => {
        if (!password) {
            setPasswordError('Password is required.');
            return;
        }
        setPasswordLoading(true);
        setPasswordError(null);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BASE}/Finance/payouts/refreshPass`,
                { password: password },
                {
                    headers: {
                        "x-access-token": token
                    },
                });

            if (response.status === 200) {
                setPasswordDialogOpen(false);
                setPassword('');
                // Submit the payout after password verification
                if (pendingPayoutData) {
                    addPayouts(pendingPayoutData, navigate);
                    setPendingPayoutData(null);
                }
            } else {
                setPasswordError('Invalid password. Please try again.');
            }
        } catch (error) {
            setPasswordError(error.response?.data?.message || 'Unable to verify password. Please try again.');
        } finally {
            setPasswordLoading(false);
        }
    };

    const handleFiles = (files) => {
        const uploadedFile = files[0];
        setFile(uploadedFile);
        setParsedPayouts([]);
        setShowConfirmation(false);
        
        // Parse file immediately
        if (uploadedFile) {
            var reader = new FileReader();
            reader.onload = async (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array' });
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];
                    const parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

                    // Assuming the first row is the header
                    const header = parsedData[0];
                    const payoutDetails = [];

                    parsedData.slice(1).forEach((row) => {
                        const payout = {};
                        row.forEach((value, index) => {
                            payout[header[index]] = value;
                        });

                        const vendorId = payout["Vendor Id"];
                        const amount = payout["Total Paid"];

                        if (vendorId && amount !== undefined && amount !== null && amount !== "") {
                            const parsedAmount = parseFloat(amount);
                            // Allow negative amounts (collections); filter out zeros and non-numeric values
                            if (!isNaN(parsedAmount) && parsedAmount !== 0) {
                                // Find vendor name
                                const vendor = vendors?.find(v => v._id === vendorId);
                                payoutDetails.push({
                                    vendorId,
                                    vendorName: vendor?.name || vendorId,
                                    amount: parsedAmount
                                });
                            }
                        }
                    });

                    setParsedPayouts(payoutDetails);
                    setShowConfirmation(true);
                    
                    // Show notification if any payouts were filtered out
                    const totalRows = parsedData.length - 1;
                    const filteredCount = totalRows - payoutDetails.length;
                    if (filteredCount > 0) {
                        notification.info({ 
                            message: `Filtered out ${filteredCount} payout(s) with zero or invalid amounts` 
                        });
                    }
                } catch (err) {
                    console.log(err);
                    notification.error({ message: "Error parsing Excel file" });
                }
            };
            reader.readAsArrayBuffer(uploadedFile);
        }
    };

    const finalBalance = vendorDueAmount !== null && payoutAmount 
        ? (vendorDueAmount - (parseFloat(payoutAmount) || 0)).toFixed(2)
        : null;

    return (
        <Container maxWidth="md" sx={{ py: 4 }}>
            <Paper
                elevation={3}
                sx={{
                    p: { xs: 3, sm: 5 },
                    borderRadius: 3,
                    backgroundColor: 'white',
                    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)'
                }}
            >
                <Box sx={{ mb: 5, display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 56,
                            height: 56,
                            borderRadius: 2,
                            backgroundColor: 'var(--secColor)',
                            color: 'white',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)'
                        }}
                    >
                        <AddCard color="white" sx={{ fontSize: 32 }} />
                    </Box>
                    <Typography 
                        variant='h4' 
                        sx={{ 
                            color: "var(--secColor)", 
                            fontWeight: 700,
                            fontSize: { xs: '1.5rem', sm: '2rem' }
                        }}
                    >
                        Add Payouts
                    </Typography>
                </Box>

                <Box sx={{ mb: 4 }}>
                    <Typography 
                        variant="subtitle1" 
                        sx={{ 
                            mb: 2, 
                            fontWeight: 600, 
                            color: 'text.primary',
                            fontSize: '1rem'
                        }}
                    >
                        Payout Method *
                    </Typography>
                    <ToggleButtonGroup
                        value={payoutMode}
                        exclusive
                        onChange={handleModeChange}
                        aria-label="payout method"
                        fullWidth
                        sx={{
                            height: 48,
                            '& .MuiToggleButton-root': {
                                borderColor: 'var(--secColor)',
                                color: 'var(--secColor)',
                                fontWeight: 600,
                                fontSize: '0.95rem',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                borderWidth: 2,
                                transition: 'all 0.2s ease-in-out',
                                '&.Mui-selected': {
                                    backgroundColor: 'var(--secColor)',
                                    color: 'white !important',
                                    borderColor: 'var(--secColor)',
                                    '&:hover': {
                                        backgroundColor: 'var(--secColor)',
                                        color: 'white !important',
                                        opacity: 0.9
                                    }
                                },
                                '&:hover': {
                                    backgroundColor: 'rgba(0, 0, 0, 0.06)',
                                    borderColor: 'var(--secColor)',
                                },
                                '&:not(:first-of-type)': {
                                    borderLeft: '2px solid var(--secColor)'
                                }
                            }
                        }}
                    >
                        <ToggleButton value="excel" aria-label="excel upload">
                            Upload Excel Sheet
                        </ToggleButton>
                        <ToggleButton value="single" aria-label="single vendor">
                            Single Vendor
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Box>

                <Box sx={{ mb: 3 }}>
                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                        Cash Cycle ID
                    </Typography>
                    <Autocomplete
                        options={cashCycles || []}
                        value={selectedCashCycle}
                        onChange={(_, value) => setSelectedCashCycle(value)}
                        getOptionLabel={(cashCycle) => cashCycle.title}
                        renderInput={(params) => (
                            <MainInput
                                {...params}
                                fullWidth
                                placeholder="Select Cash Cycle"
                            />
                        )}
                    />
                </Box>

                {payoutMode === "excel" ? (
                    <Box
                        component="form"
                        onSubmit={handleExcelSubmit}
                        sx={{ mt: 2 }}
                    >
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Upload Excel File *
                            </Typography>

                            <ReactFileReader handleFiles={handleFiles} fileTypes={[".xlsx", ".xls"]}>
                                <Button fullWidth sx={{ ...sec_button, py: 1.5 }} startIcon={<Upload />}>
                                    Upload .xlsx or .xls File
                                </Button>
                            </ReactFileReader>
                            {file && (
                                <Box sx={{ mt: 2 }}>
                                    <Chip
                                        label={file.name}
                                        onDelete={handleDeleteFile}
                                        sx={{
                                            backgroundColor: 'var(--secColor)',
                                            color: 'white',
                                            '& .MuiChip-deleteIcon': {
                                                color: 'white'
                                            }
                                        }}
                                    />
                                </Box>
                            )}
                        </Box>

                        {showConfirmation && parsedPayouts.length > 0 && (
                            <Box sx={{ mb: 3 }}>
                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                    Confirmation - Payout Summary
                                </Typography>
                                <TableContainer component={Paper} variant="outlined">
                                    <Table sx={muiTableSx}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Vendor Name</strong></TableCell>
                                                <TableCell align="right"><strong>Amount (EGP)</strong></TableCell>
                                                <TableCell align="center" sx={{ width: 80 }}><strong>Actions</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {parsedPayouts.map((payout, index) => (
                                                <TableRow key={`${payout.vendorId}-${payout.amount}-${index}`}>
                                                    <TableCell>{payout.vendorName}</TableCell>
                                                    <TableCell
                                                        align="right"
                                                        sx={{ color: payout.amount < 0 ? 'error.main' : 'inherit' }}
                                                    >
                                                        {payout.amount.toFixed(2)}
                                                    </TableCell>
                                                    <TableCell align="center">
                                                        <IconButton
                                                            onClick={() => handleRemovePayout(index)}
                                                            disabled={parsedPayouts.length <= 1}
                                                            color="error"
                                                            size="small"
                                                            sx={{
                                                                '&:hover': {
                                                                    backgroundColor: 'error.light',
                                                                    color: 'error.contrastText'
                                                                }
                                                            }}
                                                        >
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                            <TableRow>
                                                <TableCell><strong>Total</strong></TableCell>
                                                <TableCell align="right">
                                                    <strong>
                                                        {parsedPayouts.reduce((sum, p) => sum + p.amount, 0).toFixed(2)} EGP
                                                    </strong>
                                                </TableCell>
                                                <TableCell></TableCell>
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Box>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            sx={{ ...main_button, py: 1.5, mt: 2 }}
                            disabled={isLoading || !file}
                        >
                            {isLoading ? (
                                <CircularProgress size={24} color="inherit" />
                            ) : showConfirmation ? (
                                "Confirm and Add Payouts"
                            ) : (
                                "Parse and Review"
                            )}
                        </Button>
                    </Box>
                ) : (
                    <Box
                        component="form"
                        onSubmit={handleSingleVendorSubmit}
                        sx={{ mt: 2 }}
                    >
                        <Box sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                Select Vendor *
                            </Typography>
                            <Autocomplete
                                options={vendors || []}
                                value={selectedVendor}
                                onChange={(_, value) => setSelectedVendor(value)}
                                getOptionLabel={brandOptionLabel}
                                filterOptions={brandFilterOptions}
                                loading={loadingDueAmount}
                                renderInput={(params) => (
                                    <MainInput
                                        {...params}
                                        fullWidth
                                        placeholder="Select Vendor"
                                    />
                                )}
                            />
                        </Box>

                        {selectedVendor && (
                            <>
                                <Box sx={{ mb: 3, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                                            Amount Due
                                        </Typography>
                                        {vendorDueAmount !== null && !loadingDueAmount && vendorDueAmount !== 0 && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                onClick={() => setPayoutAmount(vendorDueAmount.toFixed(2))}
                                                sx={{
                                                    borderColor: 'var(--secColor)',
                                                    color: 'var(--secColor)',
                                                    textTransform: 'none',
                                                    fontSize: '0.75rem',
                                                    py: 0.5,
                                                    px: 1.5,
                                                    '&:hover': {
                                                        borderColor: 'var(--secColor)',
                                                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                                                    }
                                                }}
                                            >
                                                {vendorDueAmount < 0 ? "Collect All" : "Payout All"}
                                            </Button>
                                        )}
                                    </Box>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            color: vendorDueAmount !== null && vendorDueAmount < 0
                                                ? 'error.main'
                                                : 'var(--secColor)'
                                        }}
                                    >
                                        {loadingDueAmount ? (
                                            <CircularProgress size={20} />
                                        ) : vendorDueAmount !== null ? (
                                            `${vendorDueAmount.toFixed(2)} EGP`
                                        ) : (
                                            "Loading..."
                                        )}
                                    </Typography>
                                    {vendorDueAmount !== null && !loadingDueAmount && vendorDueAmount < 0 && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 1 }}>
                                            Negative due means the brand owes an outstanding balance. Enter a negative payout amount to record the collection.
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ mb: 3 }}>
                                    <Typography variant="body2" sx={{ mb: 1, fontWeight: 500, color: 'text.secondary' }}>
                                        Payout Amount *
                                    </Typography>
                                    <MainInput
                                        fullWidth
                                        type="number"
                                        value={payoutAmount}
                                        onChange={(e) => setPayoutAmount(e.target.value)}
                                        placeholder="Enter payout amount (negative to collect)"
                                        inputProps={{ step: 0.01 }}
                                        helperText="Use a negative amount when receiving an outstanding balance from a brand."
                                    />
                                </Box>

                                {payoutAmount && vendorDueAmount !== null && (
                                    <Box sx={{ mb: 3, p: 2, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
                                        <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                                            Final Balance After Payout
                                        </Typography>
                                        <Typography 
                                            variant="h5" 
                                            sx={{ 
                                                fontWeight: 600, 
                                                color: parseFloat(finalBalance) < 0 ? 'error.main' : 'success.main' 
                                            }}
                                        >
                                            {finalBalance} EGP
                                        </Typography>
                                    </Box>
                                )}
                            </>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            sx={{ ...main_button, py: 1.5, mt: 2 }}
                            disabled={isLoading || !selectedVendor || !payoutAmount}
                        >
                            {isLoading ? <CircularProgress size={24} color="inherit" /> : "Add Payout"}
                        </Button>
                    </Box>
                )}
            </Paper>
            
            {/* Password Verification Dialog */}
            <Dialog open={passwordDialogOpen} onClose={handlePasswordClose}>
                <DialogTitle>Password Verification Required</DialogTitle>
                <DialogContent>
                    <Typography sx={{ mb: 2 }}>Please enter your password to confirm adding payouts.</Typography>
                    <TextField
                        margin="normal"
                        label="Password"
                        type="password"
                        fullWidth
                        value={password}
                        onChange={({ target }) => setPassword(target.value)}
                        autoFocus
                        error={Boolean(passwordError)}
                        helperText={passwordError}
                        disabled={passwordLoading}
                        onKeyPress={(e) => {
                            if (e.key === 'Enter' && !passwordLoading) {
                                handlePasswordConfirm();
                            }
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handlePasswordClose} disabled={passwordLoading} sx={{ textTransform: 'none' }}>
                        Cancel
                    </Button>
                    <Button onClick={handlePasswordConfirm} disabled={passwordLoading} sx={{ ...main_button, textTransform: 'none' }}>
                        {passwordLoading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : 'Verify'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

const mapStateToProps = (state) => ({
    isLoading: state?.wait?.isLoading,
    cashCycles: state?.records?.cashCycles,
    vendors: state?.records?.vendors,
    summary: state?.records?.summary,
    token: state?.auth?.token,
});

const mapDispatchToProps = { addPayouts, getCashCycles, getVendors, getPayoutsDue };

export default connect(mapStateToProps, mapDispatchToProps)(AddPayouts);
